import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useState, useEffect, useRef } from 'react'
import {
  MessageCircle, Sun, Moon, X, Menu,
  ClipboardList, LogIn, UserPlus, Plus,
  FolderOpen, Inbox, HardHat, User,
  Settings2, LogOut,
} from 'lucide-react'
import api from '../api/axios'

export default function Navbar() {
  const { user, logout } = useAuth()
  const { theme, toggle } = useTheme()
  const navigate  = useNavigate()
  const location  = useLocation()
  const [open,       setOpen]       = useState(false)
  const [unreadMsgs, setUnreadMsgs] = useState(0)
  const pollRef = useRef(null)

  useEffect(() => { setOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!user) { setUnreadMsgs(0); return }
    const fetch = () => {
      api.get('/conversations/unread')
        .then(r => setUnreadMsgs(r.data.count || 0))
        .catch(() => {})
    }
    fetch()
    pollRef.current = setInterval(fetch, 30_000)
    return () => clearInterval(pollRef.current)
  }, [user?.userId])

  const handleLogout = () => {
    logout()
    navigate('/')
    setOpen(false)
  }

  const isActive = (path) => location.pathname === path

  const iconStyle = { display: 'flex', alignItems: 'center', gap: '.35rem' }

  return (
    <>
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      borderBottom: '1px solid var(--nav-border)',
      background: 'var(--nav-bg)',
      backdropFilter: 'blur(20px)',
      transition: 'background .25s, border-color .25s',
    }}>
      <div className="container" style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', height: '64px',
      }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none' }} onClick={() => setOpen(false)}>
          <span style={{
            fontWeight: 800, fontSize: '1rem',
            letterSpacing: '.08em', textTransform: 'uppercase',
            color: 'var(--text)',
          }}>
            1ishvar<span style={{ color: 'var(--accent)' }}>.</span>az
          </span>
        </Link>

        {/* ── Desktop nav ── */}
        <div className="nav-desktop">
          <Link to="/" className={`btn btn-ghost btn-sm ${isActive('/') ? 'active-link' : ''}`}>
            Elanlar
          </Link>
          <Link to="/workers" className={`btn btn-ghost btn-sm ${isActive('/workers') ? 'active-link' : ''}`}>
            <span style={iconStyle}><HardHat size={14} /> İşçilər</span>
          </Link>

          {!user && <>
            <Link to="/login"    className="btn btn-ghost btn-sm"><span style={iconStyle}><LogIn size={14} /> Giriş</span></Link>
            <Link to="/register" className="btn btn-primary btn-sm"><span style={iconStyle}><UserPlus size={14} /> Qeydiyyat</span></Link>
          </>}

          {user?.role === 'EMPLOYER' && <>
            <Link to="/post-job" className="btn btn-ghost btn-sm"><span style={iconStyle}><Plus size={14} /> Elan</span></Link>
            <Link to="/my-jobs"  className="btn btn-ghost btn-sm"><span style={iconStyle}><FolderOpen size={14} /> Elanlarım</span></Link>
          </>}

          {user?.role === 'WORKER' && <>
            <Link to="/my-applications" className="btn btn-ghost btn-sm">Müraciətlərim</Link>
            <Link to="/worker-profiles" className="btn btn-ghost btn-sm"><span style={iconStyle}><HardHat size={14} /> Profillərim</span></Link>
          </>}

          {user && (
            <Link to="/messages" className="btn btn-ghost btn-sm" title="Mesajlar" style={{ position: 'relative' }}>
              <MessageCircle size={16} />
              {unreadMsgs > 0 && (
                <span style={{
                  position: 'absolute', top: 2, right: 2,
                  minWidth: 16, height: 16, borderRadius: '50%',
                  background: 'var(--accent)', color: 'var(--bg)',
                  fontSize: '.55rem', fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1,
                }}>
                  {unreadMsgs > 9 ? '9+' : unreadMsgs}
                </span>
              )}
            </Link>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', gap: '.5rem',
            marginLeft: 'auto', paddingLeft: '.75rem',
            borderLeft: '1px solid var(--nav-separator)',
          }}>
            <button
              onClick={toggle}
              className="btn btn-ghost btn-sm"
              title={theme === 'dark' ? 'Light moda keç' : 'Dark moda keç'}
              aria-label={theme === 'dark' ? 'Light moda keç' : 'Dark moda keç'}
              style={{ padding: '.4rem .6rem' }}
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {user && <>
              <Link to="/profile" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: '.25rem' }}>
                <span style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text)' }}>{user.fullName}</span>
                <span style={{ fontSize: '.6rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  {user.role === 'EMPLOYER' ? 'İşəgötürən' : user.role === 'ADMIN' ? 'Admin' : 'İşçi'}
                </span>
              </Link>
              <Link to="/settings" className="btn btn-ghost btn-sm" title="Tənzimləmələr"><Settings2 size={15} /></Link>
              <button onClick={handleLogout} className="btn btn-ghost btn-sm"><span style={iconStyle}><LogOut size={14} /> Çıxış</span></button>
            </>}
          </div>
        </div>

        {/* ── Mobil: theme toggle + hamburger ── */}
        <div className="nav-mobile-icons">
          <button
            onClick={toggle}
            className="nav-hamburger"
            title={theme === 'dark' ? 'Light mod' : 'Dark mod'}
            aria-label={theme === 'dark' ? 'Light moda keç' : 'Dark moda keç'}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            className="nav-hamburger"
            onClick={() => setOpen(o => !o)}
            aria-label="Menü"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* ── Mobile dropdown menu ── */}
      <div className={`nav-mobile-menu${open ? ' open' : ''}`}>

        <Link to="/" className={`btn btn-ghost btn-sm ${isActive('/') ? 'active-link' : ''}`}>
          <span style={iconStyle}><ClipboardList size={14} /> Elanlar</span>
        </Link>
        <Link to="/workers" className={`btn btn-ghost btn-sm ${isActive('/workers') ? 'active-link' : ''}`} onClick={() => setOpen(false)}>
          <span style={iconStyle}><HardHat size={14} /> İşçilər</span>
        </Link>

        {!user && <>
          <div className="nav-mobile-divider" />
          <Link to="/login"    className="btn btn-ghost btn-sm"><span style={iconStyle}><LogIn size={14} /> Giriş</span></Link>
          <Link to="/register" className="btn btn-primary btn-sm"><span style={iconStyle}><UserPlus size={14} /> Qeydiyyat</span></Link>
        </>}

        {user?.role === 'EMPLOYER' && <>
          <div className="nav-mobile-divider" />
          <Link to="/post-job" className="btn btn-ghost btn-sm"><span style={iconStyle}><Plus size={14} /> Elan yerləşdir</span></Link>
          <Link to="/my-jobs"  className="btn btn-ghost btn-sm"><span style={iconStyle}><FolderOpen size={14} /> Elanlarım</span></Link>
        </>}

        {user?.role === 'WORKER' && <>
          <div className="nav-mobile-divider" />
          <Link to="/my-applications" className="btn btn-ghost btn-sm"><span style={iconStyle}><Inbox size={14} /> Müraciətlərim</span></Link>
          <Link to="/worker-profiles" className="btn btn-ghost btn-sm"><span style={iconStyle}><HardHat size={14} /> Profillərim</span></Link>
        </>}

        {user && <>
          <div className="nav-mobile-divider" />
          <Link to="/messages" className="btn btn-ghost btn-sm" style={{ position: 'relative' }}>
            <span style={iconStyle}>
              <MessageCircle size={14} /> Mesajlar
            </span>
            {unreadMsgs > 0 && (
              <span style={{
                marginLeft: '.4rem',
                minWidth: 18, height: 18, borderRadius: '50%',
                background: 'var(--accent)', color: 'var(--bg)',
                fontSize: '.62rem', fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {unreadMsgs > 9 ? '9+' : unreadMsgs}
              </span>
            )}
          </Link>
          <Link to="/profile" className="btn btn-ghost btn-sm">
            <span style={iconStyle}>
              <User size={14} /> {user.fullName}
              <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>
                ({user.role === 'EMPLOYER' ? 'İşəgötürən' : user.role === 'ADMIN' ? 'Admin' : 'İşçi'})
              </span>
            </span>
          </Link>
          <Link to="/settings" className="btn btn-ghost btn-sm"><span style={iconStyle}><Settings2 size={14} /> Tənzimləmələr</span></Link>
          <button onClick={handleLogout} className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', borderColor: 'rgba(248,113,113,.2)' }}>
            <span style={iconStyle}><LogOut size={14} /> Çıxış</span>
          </button>
        </>}

        <div className="nav-mobile-divider" />
        <button onClick={toggle} className="btn btn-ghost btn-sm" style={{ justifyContent: 'flex-start' }}>
          <span style={iconStyle}>
            {theme === 'dark' ? <><Sun size={14} /> Light mod</> : <><Moon size={14} /> Dark mod</>}
          </span>
        </button>
      </div>

      <style>{`
        .active-link { color: var(--accent) !important; border-color: rgba(var(--accent-rgb),.2) !important; }
      `}</style>
    </nav>

    {/* ─── Bottom tab bar (mobile only, rendered via CSS display:none/flex) ─── */}
    <div className="bottom-tab-bar">
      <Link to="/" className={`bottom-tab-btn${isActive('/') ? ' active' : ''}`}>
        <ClipboardList size={21} />
        <span>Elanlar</span>
      </Link>

      {user && (
        <Link to="/messages" className={`bottom-tab-btn${isActive('/messages') ? ' active' : ''}`}>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <MessageCircle size={21} />
            {unreadMsgs > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -6,
                minWidth: 14, height: 14, borderRadius: '50%',
                background: 'var(--danger)', color: '#fff',
                fontSize: '.5rem', fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1,
              }}>{unreadMsgs > 9 ? '9+' : unreadMsgs}</span>
            )}
          </span>
          <span>Mesajlar</span>
        </Link>
      )}

      {user?.role === 'WORKER' && (
        <Link to="/my-applications" className={`bottom-tab-btn${isActive('/my-applications') ? ' active' : ''}`}>
          <Inbox size={21} />
          <span>Müraciətlər</span>
        </Link>
      )}

      {user?.role === 'EMPLOYER' && (
        <Link to="/my-jobs" className={`bottom-tab-btn${isActive('/my-jobs') ? ' active' : ''}`}>
          <FolderOpen size={21} />
          <span>Elanlarım</span>
        </Link>
      )}

      {user ? (
        <Link to="/profile" className={`bottom-tab-btn${isActive('/profile') ? ' active' : ''}`}>
          <User size={21} />
          <span>Profil</span>
        </Link>
      ) : (
        <>
          <Link to="/login" className={`bottom-tab-btn${isActive('/login') ? ' active' : ''}`}>
            <LogIn size={21} />
            <span>Giriş</span>
          </Link>
          <Link to="/register" className={`bottom-tab-btn${isActive('/register') ? ' active' : ''}`}>
            <UserPlus size={21} />
            <span>Qeydiyyat</span>
          </Link>
        </>
      )}
    </div>
    </>
  )
}
